import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useLocalePrefix } from '@/lib/hooks/useLocaleNavigate';
import { cn } from '@/lib/utils';
import { NavNode, firstTarget, flatten } from './navTree';

interface NavGroupListProps {
  /** The open top menu's children: its second level (groups or pages). */
  groups: NavNode[];
  /** Exact-match test for one node against the current address. */
  isActive: (node: NavNode) => boolean;
  /** Called after a page link is followed (the narrow Menu closes itself). */
  onNavigate?: () => void;
  /** Indent of the first level, in px (the narrow Menu nests under its top menu). */
  baseIndent?: number;
}

/**
 * GEN-04-04 — the second and third levels of one top menu, shared by the left
 * sidebar and the narrow-screen Menu (GEN-04-07) so both behave the same:
 * 13 px text, no icons, no connecting lines, items indented 14 px per level,
 * the current page alone on a light pink background.
 *
 * GEN-04-17: groups open one at a time — opening one closes the open sibling,
 * clicking the open group's title closes it, and the page in use stays
 * highlighted. A group whose first page is elsewhere also navigates there
 * when opened (one click does both jobs); a group that is also a page shows
 * that page first (GEN-04-04 (5)).
 */
/**
 * SAL-24-02 REPORT SEARCH: the box above a group's pages shows only the pages
 * whose name — or another name listed in `aliases` (Studio Manager / staging
 * names) — contains the text, any case, any part. Empty box = all; nothing
 * found = 'No report matches.'
 */
function SearchableChildren({
  node,
  depth,
  indent,
  renderNode,
}: {
  node: NavNode;
  depth: number;
  indent: (depth: number) => number;
  renderNode: (node: NavNode, depth: number, siblings: NavNode[]) => React.ReactNode;
}) {
  const { t } = useTranslation('nav');
  const [query, setQuery] = useState('');
  const children = node.children ?? [];
  const needle = query.trim().toLowerCase();
  const shown = needle
    ? children.filter((child) =>
        [t(child.labelKey), ...(child.aliases ?? [])].some((name) => name.toLowerCase().includes(needle)),
      )
    : children;
  return (
    <div className="mt-0 space-y-0">
      <div style={{ paddingLeft: indent(depth + 1) }} className="pr-2 pb-1">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('reportSearchPlaceholder')}
          aria-label={t('reportSearchPlaceholder')}
          data-testid="nav-report-search"
          className="w-full rounded-md border border-[#E2E8F0] bg-white px-2 py-1 text-[13px] leading-5 text-[#313131] placeholder:text-[#9CA3AF] focus:border-[#FF8189] focus:outline-none"
        />
      </div>
      {shown.length === 0 ? (
        <p style={{ paddingLeft: indent(depth + 1) }} className="py-1 pr-2 text-[13px] text-[#6B7280]">
          {t('noReportMatches')}
        </p>
      ) : (
        shown.map((child) => renderNode(child, depth + 1, children))
      )}
    </div>
  );
}

export function NavGroupList({ groups, isActive, onNavigate, baseIndent = 8 }: NavGroupListProps) {
  const { t } = useTranslation('nav');
  const prefix = useLocalePrefix();

  // Per-group open state. Undefined → open iff it holds the current page,
  // which is what makes a deep link arrive with its group already unfolded.
  const [openNodes, setOpenNodes] = useState<Record<string, boolean>>({});

  const subtreeActive = (node: NavNode): boolean => flatten(node).some(isActive);
  const indent = (depth: number) => baseIndent + depth * 14;

  const itemClasses = (active: boolean) =>
    cn(
      'block w-full rounded-md py-1.5 pr-2 text-left text-[13px] leading-5 transition-colors',
      active ? 'bg-[#FFF1F2] font-medium text-[#E65C6A]' : 'text-[#313131] hover:bg-[#F7F7F7]',
    );

  const renderLeaf = (node: NavNode, depth: number) => {
    const active = isActive(node);
    return (
      <Link
        key={node.id}
        to={prefix + node.href}
        onClick={onNavigate}
        data-testid={`nav-${node.id}`}
        data-active={active || undefined}
        aria-current={active ? 'page' : undefined}
        className={itemClasses(active)}
        style={{ paddingLeft: indent(depth) }}
      >
        {t(node.labelKey)}
      </Link>
    );
  };

  const renderGroup = (node: NavNode, depth: number, siblings: NavNode[]) => {
    const active = subtreeActive(node);
    const open = openNodes[node.id] ?? active;
    const target = firstTarget(node);

    const handleClick = () => {
      if (open) {
        setOpenNodes((prev) => ({ ...prev, [node.id]: false }));
        return;
      }
      setOpenNodes((prev) => {
        const next = { ...prev, [node.id]: true };
        for (const sibling of siblings) {
          if (sibling.id !== node.id && sibling.children?.length) next[sibling.id] = false;
        }
        return next;
      });
    };

    // Unfolding a closed group also lands on its first page — but not when the
    // user is already on a page inside it (that would navigate them away from
    // their own work just for expanding a group).
    const navigatesTo = !open && !active && target?.href ? prefix + target.href : undefined;

    const rowClasses = cn(
      'flex w-full items-center gap-1 rounded-md py-1 pr-1 text-left text-[13px] leading-5 font-medium transition-colors',
      active ? 'bg-[#FFE4E6] font-bold text-[#E65C6A]' : 'text-[#313131] hover:bg-[#F3F4F6]',
    );
    const rowStyle = { paddingLeft: indent(depth) };
    const rowInner = (
      <>
        <span className="flex-1">{t(node.labelKey)}</span>
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 opacity-60" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 opacity-60" />
        )}
      </>
    );

    return (
      <div key={node.id}>
        {navigatesTo ? (
          <Link
            to={navigatesTo}
            onClick={() => {
              handleClick();
              onNavigate?.();
            }}
            aria-expanded={open}
            data-testid={`nav-${node.id}`}
            data-active={active || undefined}
            className={rowClasses}
            style={rowStyle}
          >
            {rowInner}
          </Link>
        ) : (
          <button
            type="button"
            onClick={handleClick}
            aria-expanded={open}
            data-testid={`nav-${node.id}`}
            data-active={active || undefined}
            className={rowClasses}
            style={rowStyle}
          >
            {rowInner}
          </button>
        )}
        {open && node.searchBox ? (
          <SearchableChildren
            node={node}
            depth={depth}
            indent={indent}
            renderNode={renderNode}
          />
        ) : open ? (
          <div className="mt-0 space-y-0">
            {/* GEN-04-04 (5): a group that is also a page shows that page first. */}
            {node.href && renderLeaf({ ...node, id: `${node.id}-page`, labelKey: node.pageLabelKey ?? node.labelKey, children: undefined }, depth + 1)}
            {(node.children ?? []).map((child) => renderNode(child, depth + 1, node.children ?? []))}
          </div>
        ) : null}
      </div>
    );
  };

  const renderNode = (node: NavNode, depth: number, siblings: NavNode[]): React.ReactNode =>
    node.children?.length ? renderGroup(node, depth, siblings) : renderLeaf(node, depth);

  return <>{groups.map((node) => renderNode(node, 0, groups))}</>;
}
